import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
    Grid,
    Box,
    TextField,
    MenuItem,
    Autocomplete,
    IconButton,
    Tooltip,
} from "@mui/material";
import { useCallback, useState } from "react";

import { HeaderPage } from "../../components/Header/HeaderPage";
import PageWrapper from "../../components/PageWrapper";
import { buildUrl } from "../../helpers/buildUrl";
import { usePaginatedData } from "../../hooks/usePaginatedData";
import { useQuery } from "../../hooks/useQuery";
import { Acquirer } from "../../interfaces/acquirer";
import { Customer } from "../../interfaces/customer";
import { DashboardFinancialSummary } from "../../interfaces/dashboard";
import { Transaction } from "../../interfaces/transaction";
import { Withdrawal } from "../../interfaces/withdrawal";
import { ActiveIntegrations } from "./ActiveIntegrations";
import { DashboardMetrics } from "./DashboardMetrics";
import { PendingWithdrawals } from "./PendingWithdrawals";
import { TransactionsHistory } from "./TransactionsHistory";

export default function AcquirerDashboard() {
    const [days, setDays] = useState<string>("");
    const [customer, setCustomer] = useState<string>("");
    const [txStatus, setTxStatus] = useState<string>("");
    const [txPaymentMethod, setTxPaymentMethod] = useState<string>("");

    const { data: customers, isLoading: customersLoading } =
        useQuery<Customer[]>("/customers");

    const {
        data: metricsData,
        isLoading: metricsLoading,
        refresh: refreshMetrics,
    } = useQuery<DashboardFinancialSummary>(
        buildUrl("/dashboard/financial/overview", {
            customer_id: customer,
            days,
        })
    );

    const {
        data: withdrawals,
        isLoading: withdrawalsLoading,
        refresh: refreshWithdrawals,
    } = usePaginatedData<Withdrawal>(
        buildUrl("/withdrawal", {
            status: "pending",
            per_page: 3,
            customer_id: customer,
            days,
        })
    );

    const {
        data: integrations,
        isLoading: integrationsLoading,
        refresh: refreshIntegrations,
    } = usePaginatedData<Acquirer>(
        "/acquirers?type=gateway&include=fees&active=1&per_page=2"
    );

    const {
        data: transactions,
        isLoading: txLoading,
        meta: txMeta,
        setPage: setTxPage,
        refresh: refreshTransactions,
    } = usePaginatedData<Transaction>(
        buildUrl("/global-transactions", {
            status: txStatus,
            payment_method: txPaymentMethod,
            customer_id: customer,
            days,
        })
    );

    const getCustomersOptions = useCallback(() => {
        if (!customers?.length) return [];

        return customers.map((customer) => ({
            id: customer.id,
            label: customer.name,
        }));
    }, [customers]);

    const isRefreshing =
        metricsLoading ||
        withdrawalsLoading ||
        integrationsLoading ||
        txLoading;

    const handleStatusChange = (status: string) => {
        setTxStatus(status);
        setTxPage(1);
    };

    const handlePaymentMethodChange = (method: string) => {
        setTxPaymentMethod(method);
        setTxPage(1);
    };

    const handleRefresh = () => {
        if (isRefreshing) return;

        refreshMetrics();
        refreshWithdrawals();
        refreshIntegrations();
        refreshTransactions();
    };

    return (
        <PageWrapper>
            <Box flex={1}>
                <Box display="flex" justifyContent="space-between" mb={3}>
                    <HeaderPage
                        title="Visão Geral Financeira"
                        subtitle="Dados consolidados de todas as transações"
                    />

                    <Box display="flex" alignItems="flex-start" gap={2}>
                        <Tooltip title="Atualizar dados">
                            <IconButton
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>

                        <Autocomplete
                            options={getCustomersOptions()}
                            onChange={(_, value) => {
                                setCustomer(value?.id ?? "");
                            }}
                            loading={customersLoading}
                            disabled={customersLoading}
                            sx={{ minWidth: 200 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Buscar cliente"
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { paddingLeft: 14 },
                                        startAdornment: (
                                            <SearchIcon fontSize="small" />
                                        ),
                                    }}
                                />
                            )}
                        />

                        <TextField
                            select
                            value={days}
                            SelectProps={{ displayEmpty: true }}
                            onChange={(e) => setDays(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">Todo o período</MenuItem>
                            <MenuItem value="7">Últimos 7 dias</MenuItem>
                            <MenuItem value="30">Últimos 30 dias</MenuItem>
                            <MenuItem value="90">Últimos 90 dias</MenuItem>
                        </TextField>
                    </Box>
                </Box>

                <DashboardMetrics
                    data={metricsData}
                    isLoading={metricsLoading}
                />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <PendingWithdrawals
                            data={withdrawals}
                            isLoading={withdrawalsLoading}
                            onRefresh={refreshWithdrawals}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ActiveIntegrations
                            data={integrations}
                            isLoading={integrationsLoading}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TransactionsHistory
                            data={transactions}
                            isLoading={txLoading}
                            meta={txMeta}
                            setPage={setTxPage}
                            status={txStatus}
                            onStatusChange={handleStatusChange}
                            paymentMethod={txPaymentMethod}
                            onPaymentMethodChange={handlePaymentMethodChange}
                        />
                    </Grid>
                </Grid>
            </Box>
        </PageWrapper>
    );
}
